import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import { format } from 'date-fns';
import { toast } from 'material-react-toastify';

import { Text } from '@/components/shared/Text/Text';
import WithLoading from '@/lib/hoc/WithLoading/WithLoading';
import { useCoachCabinetStore } from '@/lib/store/coachCabinetStore';
import { Timeslot as TimeslotType } from '@addmile/backend-client';
import { UseQueryResult } from '@tanstack/react-query';

import { useCoachTimeslots, useDeleteCoachTimeslot } from '../hooks/useCoachTimeslots';
import Timeslot from './components/Timeslot/Timeslot';

const CoachCalendar = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const coach = useCoachCabinetStore((state) => state.coach);

  const {
    data: timeslots = [],
    isLoading,
    error,
    isError,
  } = useCoachTimeslots(coach?.id, selectedDate, coach?.timezone) as UseQueryResult<TimeslotType[]>;

  const deleteTimeslotMutation = useDeleteCoachTimeslot();

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  const handleDeleteTimeslot = async (timeslotId: number) => {
    if (!coach?.id) return;

    deleteTimeslotMutation.mutate(
      { coachId: coach.id, timeslotId },
      {
        onSuccess: () => {
          toast.success('Timeslot deleted successfully');
        },
        onError: (error) => {
          toast.error(`Failed to delete timeslot: ${error.message}`);
        },
      },
    );
  };

  const formattedDate = selectedDate ? format(selectedDate, 'MMMM d, yyyy') : '';

  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'An error occurred while fetching timeslots';

  const hasTimeslots = timeslots.length > 0;

  return (
    <div className="animation-fade-in">
      <Text.H3 weight="bold">Coach Calendar</Text.H3>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 mt-4">
        <div className="bg-white rounded-lg shadow-sm p-4 h-fit">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={{ before: new Date() }}
            className="border-0"
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 min-h-[300px]">
          {selectedDate && (
            <Text.H4 className="mb-4 font-semibold border-b pb-3">
              Timeslots for {formattedDate}
            </Text.H4>
          )}

          <div className="min-h-[200px]">
            {isError && (
              <div className="text-red-500 p-4 bg-red-50 rounded-md">
                <Text.Regular>{errorMessage}</Text.Regular>
              </div>
            )}

            <WithLoading
              isLoading={isLoading}
              className="min-h-[200px] flex items-center justify-center"
            >
              {!selectedDate && !isLoading && !isError && (
                <div className="flex items-center justify-center h-full min-h-[200px]">
                  <Text.Regular className="text-neutral-50">
                    Please select a date to view available timeslots
                  </Text.Regular>
                </div>
              )}

              {selectedDate && !isLoading && !isError && !hasTimeslots && (
                <div className="flex flex-col items-center justify-center h-full min-h-[200px]">
                  <Text.Regular className="text-neutral-50 mb-2">
                    No timeslots available for this day
                  </Text.Regular>
                  <Text.Small className="text-neutral-40">
                    You can create new timeslots from the "Add Timeslots" tab
                  </Text.Small>
                </div>
              )}

              {selectedDate && !isLoading && hasTimeslots && (
                <ul className="space-y-3 max-h-[500px] overflow-y-auto">
                  {timeslots.map((slot) => (
                    <Timeslot
                      key={slot.id}
                      slot={slot}
                      timezone={coach?.timezone}
                      handleDeleteTimeslot={handleDeleteTimeslot}
                      isDeleting={
                        deleteTimeslotMutation.isPending &&
                        deleteTimeslotMutation.variables?.timeslotId === slot.id
                      }
                    />
                  ))}
                </ul>
              )}
            </WithLoading>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachCalendar;
